/**
 * Utility hook to parse and normalize patient plan API responses
 * Handles both old and new API response formats
 */

/**
 * Extract pagination data from API response
 * @param {Object} patientPlanList - Raw API response
 * @returns {Object} Normalized pagination data
 */
export const usePaginationParser = (patientPlanList) => {
    // Handle nested API response structure
    // New nested format might be an array: [{ docs: [...], ... }]
    // Or a pagination object: { docs: [...], ... }
    // Or a single plan: { _id: "...", ... }
    const paginationData = Array.isArray(patientPlanList)
        ? patientPlanList
        : (patientPlanList?.data || patientPlanList || {});

    const patientPlans = paginationData?.docs || (paginationData?._id ? [paginationData] : []);

    const paginationInfo = {
        totalDocs: paginationData?.totalDocs || patientPlans.length,
        totalPages: paginationData?.totalPages || 1,
        currentPage: paginationData?.page || 1,
        hasNextPage: paginationData?.hasNextPage || false,
        hasPrevPage: paginationData?.hasPrevPage || false,
        limit: paginationData?.limit || 10,
    };

    return {
        paginationData,
        patientPlans,
        paginationInfo,
        hasMultiplePlans: patientPlans.length > 1,
    };
};

/**
 * Extract selected patient plan data
 * @param {Array} patientPlans - Array of patient plans
 * @param {number} selectedPlanIndex - Index of selected plan
 * @returns {Object} Selected patient plan data
 */
export const useSelectedPatientPlan = (patientPlans, selectedPlanIndex) => {
    const actualPatientData = patientPlans[selectedPlanIndex] || patientPlans[0] || {};
    const patientPlanId = actualPatientData?._id || null;

    // Extract customer data (handles both nested and flat structures)
    const customerData = actualPatientData?.customerId || {
        name: actualPatientData?.customerName,
        email: actualPatientData?.customerEmail,
        age: actualPatientData?.customerAge,
        phoneNo: actualPatientData?.customerPhoneNo,
        gender: actualPatientData?.customerGender,
        birthDate: actualPatientData?.customerBirthdate,
    };

    return {
        actualPatientData,
        patientPlanId,
        customerData,
    };
};
